/**
 * Aufrufe der Sicherungsuebersicht -- F5-01.
 *
 * Die Zugangsdaten stehen **nicht** hier, sondern unter
 * `features/tokens/` -- sie werden unter Verwaltung > Tokenmanagement
 * gepflegt und haben mit dem taeglichen Blick auf den Bestand nichts zu tun.
 * Die Aufrufe dafuer liegen trotzdem im selben Namensraum des Servers
 * (`/backup/providers`), weil sie zu diesem Bestand gehoeren.
 */

import type { components } from '../../api/schema';
import { request } from '../../lib/apiClient';

export type BackupDevice = components['schemas']['BackupDeviceRead'];
export type BackupDeviceDetail = components['schemas']['BackupDeviceDetail'];
export type BackupEvent = components['schemas']['BackupEventRead'];
export type BackupJob = components['schemas']['BackupJobRead'];
export type BackupSummary = components['schemas']['BackupSummary'];
export type BackupSyncResult = components['schemas']['BackupSyncResult'];
export type BackupVerification = components['schemas']['BackupVerificationResult'];
export type BackupStatus = components['schemas']['BackupStatus'];

/**
 * Die Zustaende -- deckungsgleich mit `BackupStatus` im Server.
 *
 * Die Reihenfolge ist die der Dringlichkeit und nicht das Alphabet: Sie
 * bestimmt, in welcher Folge die Zahlen ueber der Liste stehen, und oben
 * steht, was jemanden heute noch beschaeftigt.
 */
export const BACKUP_STATUSES = [
  'failed',
  'completed_with_errors',
  'unknown',
  'in_process',
  'completed',
] as const;

export interface DeviceFilters {
  customer_id?: string;
  status?: BackupStatus;
  only_failing?: boolean;
}

export function fetchBackupSummary(): Promise<BackupSummary> {
  return request<BackupSummary>('/backup/summary');
}

export function fetchBackupDevices(filters: DeviceFilters = {}): Promise<BackupDevice[]> {
  return request<BackupDevice[]>('/backup/devices', { query: { ...filters } });
}

/**
 * Die Geraete, um die es in einem Ticket geht -- F5-01, Anforderung 3.
 *
 * Leere Liste heisst: kein Backup-Ticket. Der Aufruf laeuft ueber dieselbe
 * Adresse wie die Liste, weil er dieselbe Antwortform hat -- eine zweite
 * Adresse fuer dieselben Zeilen liefe beim naechsten Feld auseinander.
 */
export function fetchBackupDevicesForTicket(ticketId: string): Promise<BackupDevice[]> {
  return request<BackupDevice[]>('/backup/devices', { query: { ticket_id: ticketId } });
}

export function fetchBackupDevice(deviceId: string): Promise<BackupDeviceDetail> {
  return request<BackupDeviceDetail>(`/backup/devices/${deviceId}`);
}

export function syncBackupDevices(): Promise<BackupSyncResult> {
  return request<BackupSyncResult>('/backup/sync', { method: 'POST' });
}

export function verifyBackupState(): Promise<BackupVerification> {
  return request<BackupVerification>('/backup/verification');
}

export function restartBackup(deviceId: string, ticketId?: string): Promise<BackupJob> {
  return request<BackupJob>(`/backup/devices/${deviceId}/restart`, {
    method: 'POST',
    body: ticketId ? { ticket_id: ticketId } : {},
  });
}

export function fetchBackupJobs(deviceId?: string): Promise<BackupJob[]> {
  return request<BackupJob[]>('/backup/jobs', { query: { device_id: deviceId } });
}

export function fetchBackupJob(jobId: string): Promise<BackupJob> {
  return request<BackupJob>(`/backup/jobs/${jobId}`);
}
